import { useState } from 'react';
import type { ServerDoc } from '@panel/shared';
import { api } from '../lib/api.js';
import { IconArrowUp, IconClose, IconMap } from './Icons.js';
import { MapPreview } from './MapPreview.js';

interface Props {
  server: ServerDoc;
}

export default function MapQueue({ server }: Props) {
  const [busy, setBusy] = useState(false);

  const removeAt = async (idx: number) => {
    setBusy(true);
    try {
      const next = [...server.maps];
      next.splice(idx, 1);
      await api.post(`/api/servers/${server.Server_ID}/maps`, { maps: next, mode: 'replace' });
    } catch (err) { alert((err as Error).message); }
    finally { setBusy(false); }
  };

  const moveUp = async (idx: number) => {
    if (idx === 0) return;
    setBusy(true);
    try {
      const next = [...server.maps];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      await api.post(`/api/servers/${server.Server_ID}/maps`, { maps: next, mode: 'replace' });
    } finally { setBusy(false); }
  };

  return (
    <div className="card p-4">
      <header className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-fg">Map Queue</h3>
        <span className="text-xs text-fg-dim tabular-nums">
          {server.maps.length} queued
        </span>
      </header>

      {server.Server_map && (
        <div className="mb-3 p-3 rounded-lg bg-accent/5 border border-accent/20 flex gap-3">
          <MapPreview url={server.Server_map} size={64} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1">Currently loaded</div>
            <div className="font-mono text-[11px] text-fg break-all">{server.Server_map}</div>
          </div>
        </div>
      )}

      {server.maps.length === 0 ? (
        <EmptyState />
      ) : (
        <ol className="space-y-1">
          {server.maps.map((url, i) => (
            <li key={i} className="flex items-center gap-3 group rounded-md px-2 py-2 hover:bg-bg-hover/50 transition-colors">
              <span className="text-xs text-fg-dim w-5 text-right tabular-nums shrink-0">{i + 1}</span>
              <MapPreview url={url} size={48} className="shrink-0" />
              <span className="flex-1 font-mono text-[11px] text-fg-muted truncate min-w-0" title={url}>{url}</span>
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button
                  onClick={() => moveUp(i)}
                  disabled={busy || i === 0}
                  className="btn-icon !w-7 !h-7 disabled:opacity-30"
                  title="Move up"
                  aria-label="Move up"
                >
                  <IconArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => removeAt(i)}
                  disabled={busy}
                  className="btn-icon !w-7 !h-7 hover:!text-status-error"
                  title="Remove"
                  aria-label="Remove"
                >
                  <IconClose className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-6 flex flex-col items-center text-center gap-2">
      <div className="w-10 h-10 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
        <IconMap />
      </div>
      <div>
        <div className="text-sm text-fg-muted">Queue is empty</div>
        <div className="text-xs text-fg-dim mt-1 max-w-xs">
          Paste rustmaps URLs into the Console to add them to the rotation.
        </div>
      </div>
    </div>
  );
}
